import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import './Admin.css';

function Admin() {
  const [urls, setUrls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ longUrl: '' });
  const [showAddForm, setShowAddForm] = useState(false);
  const [newUrl, setNewUrl] = useState({ longUrl: '', customCode: '' });

  // Fetch URLs
  useEffect(() => {
    fetchUrls();
  }, []);

  const fetchUrls = async () => {
    try {
      const response = await axios.get('/api/url/all');
      console.log('Raw response data:', response.data);
      
      // Ensure we have the correct data structure
      const formattedUrls = response.data.map(url => {
        // If url is an object with longUrl property
        if (typeof url === 'object' && url.longUrl) {
          return {
            code: url.code || '',
            longUrl: typeof url.longUrl === 'string' ? url.longUrl : String(url.longUrl),
            shortUrl: url.shortUrl || `${process.env.BASE_URL}/${url.code}`
          };
        }
        // If url is a string
        return {
          code: url.code || '',
          longUrl: String(url),
          shortUrl: url.shortUrl || ''
        };
      });
      
      console.log('Formatted URLs:', formattedUrls);
      setUrls(formattedUrls);
      setError(null);
    } catch (err) {
      console.error('Error details:', err);
      setError('Error fetching URLs: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  };

  // Handle edit
  const handleEdit = (url) => {
    setEditingId(url.code);
    setEditForm({ longUrl: url.longUrl });
  };

  // Handle save edit
  const handleSaveEdit = async (code) => {
    try {
      const response = await axios.put(`/api/url/${code}`, editForm);
      setUrls(urls.map(url => url.code === code ? response.data : url));
      setEditingId(null);
      setEditForm({ longUrl: '' });
      setError(null);
    } catch (err) {
      setError('Error updating URL: ' + (err.response?.data?.error || err.message));
    }
  };

  // Handle delete
  const handleDelete = async (code) => {
    if (window.confirm('Are you sure you want to delete this URL?')) {
      try {
        await axios.delete(`/api/url/${code}`);
        setUrls(urls.filter(url => url.code !== code));
        setError(null);
      } catch (err) {
        setError('Error deleting URL: ' + (err.response?.data?.error || err.message));
      }
    }
  };

  // Handle add new URL
  const handleAdd = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post('/api/url/shorten', newUrl);
      setUrls([response.data, ...urls]);
      setNewUrl({ longUrl: '', customCode: '' });
      setShowAddForm(false);
      setError(null);
    } catch (err) {
      setError('Error adding URL: ' + (err.response?.data?.error || err.message));
    }
  };

  // Handle copy to clipboard
  const handleCopy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      alert('Copied to clipboard!');
    } catch (err) {
      console.error('Failed to copy:', err);
      alert('Failed to copy to clipboard');
    }
  };

  // Filter URLs based on search term
  const filteredUrls = urls.filter(url => {
    const searchTermLower = searchTerm.toLowerCase();
    return (
      (typeof url.longUrl === 'string' && url.longUrl.toLowerCase().includes(searchTermLower)) ||
      (typeof url.code === 'string' && url.code.toLowerCase().includes(searchTermLower)) ||
      (typeof url.shortUrl === 'string' && url.shortUrl.toLowerCase().includes(searchTermLower))
    );
  });

  if (loading) {
    return <div className="admin-loading">Loading...</div>;
  }

  return (
    <div className="admin-container">
      <div className="admin-header">
        <h1>URL Shortener Admin</h1>
        <Link to="/shorturl" className="back-link">← Back to Shortener</Link>
      </div>

      {error && <div className="admin-error">{error}</div>}

      <div className="admin-controls">
        <div className="search-bar">
          <input
            type="text"
            className="search-input"
            placeholder="Search URLs..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <button 
          className="add-button"
          onClick={() => setShowAddForm(!showAddForm)}
        >
          {showAddForm ? 'Cancel' : 'Add New URL'}
        </button>
      </div>

      {showAddForm && (
        <form className="add-form" onSubmit={handleAdd}>
          <input
            type="text"
            className="add-input"
            placeholder="Enter long URL"
            value={newUrl.longUrl}
            onChange={(e) => setNewUrl({ ...newUrl, longUrl: e.target.value })}
            required
          />
          <input
            type="text"
            className="add-input"
            placeholder="Custom code (optional)"
            value={newUrl.customCode}
            onChange={(e) => setNewUrl({ ...newUrl, customCode: e.target.value })}
          />
          <button type="submit" className="add-button">Add URL</button>
        </form>
      )}

      <div className="url-stats">
        <h2>Total URLs: {urls.length}</h2>
      </div>

      <div className="url-list">
        <table>
          <thead>
            <tr>
              <th>Short URL</th>
              <th>Original URL</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredUrls.map(url => (
              <tr key={url.code}>
                <td>
                  <a href={url.shortUrl} target="_blank" rel="noopener noreferrer">
                    {url.shortUrl}
                  </a>
                  <button
                    className="copy-button"
                    onClick={() => handleCopy(url.shortUrl)}
                  >
                    Copy
                  </button>
                </td>
                <td>
                  {editingId === url.code ? (
                    <input
                      type="text"
                      className="edit-input"
                      value={editForm.longUrl}
                      onChange={(e) => setEditForm({ longUrl: e.target.value })}
                    />
                  ) : (
                    <a href={url.longUrl} target="_blank" rel="noopener noreferrer">
                      {url.longUrl}
                    </a>
                  )}
                </td>
                <td>
                  <div className="action-buttons">
                    {editingId === url.code ? (
                      <>
                        <button
                          className="save-button"
                          onClick={() => handleSaveEdit(url.code)}
                        >
                          Save
                        </button>
                        <button
                          className="cancel-button"
                          onClick={() => setEditingId(null)}
                        >
                          Cancel
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          className="edit-button"
                          onClick={() => handleEdit(url)}
                        >
                          Edit
                        </button>
                        <button
                          className="delete-button"
                          onClick={() => handleDelete(url.code)}
                        >
                          Delete
                        </button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default Admin; 